import React, { useContext, useEffect, useState } from 'react';  
import ResultContext from '../context/ResultContext';  
import Answer from '../components/Answer';  
import { Link, useNavigate, useParams } from 'react-router-dom';  
import '../styles/PremiumResult.css';  
import Spinner from '../components/Spinner';  
import Navbar from '../components/Navbar';  
import { useAuth } from '../context/AuthContext';
import { premiumQuizAPI, batchAPI } from '../services/api';
import html2pdf from 'html2pdf.js';  

export default function PremiumResult() {  
  const formatDate = (timestamp) => {  
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];  
    const date = new Date(timestamp);  
    const day = date.getDate();  
    const month = months[date.getMonth()];  
    const year = date.getFullYear();  
    return `${day} ${month} ${year}`;  
  };  

  const navigate = useNavigate();
  const { user } = useAuth();
  const { quizData, score, responses } = useContext(ResultContext);  
  
  const [batchQuizzes, setBatchQuizzes] = useState([]);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userAttempts, setUserAttempts] = useState([]);

  // Get batchId from quizData
  const batchId = quizData?.batchId._id;

  useEffect(() => {
    // console.log("Quiz Data in content:", quizData)
    // console.log("Batch ID in quiz Data:", quizData.batchId)

    if (batchId) {
      fetchBatchDetails();
      fetchBatchQuizzes();
      if (user) {
        fetchUserAttempts();
      }
    }
  }, [batchId, user]);

  // Fetch batch details
  const fetchBatchDetails = async () => {
    try {
      const response = await batchAPI.getBatchDetails(batchId);
      if (response.data.success) {
        setBatch(response.data.batch);
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
    }
  };

  // Fetch other quizzes in this batch
  const fetchBatchQuizzes = async () => {
    try {
      setLoading(true);
      const response = await premiumQuizAPI.getBatchQuizzes(batchId, 1, 10);
      
      if (response.data.success) {
        // Filter out the current quiz
        const otherQuizzes = response.data.quizzes.filter(
          quiz => quiz._id !== quizData._id
        );
        setBatchQuizzes(otherQuizzes);
      }
    } catch (error) {
      console.error('Error fetching batch quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's previous attempts
  const fetchUserAttempts = async () => {
    try {
      const response = await premiumQuizAPI.getUserAttempts(user.id, batchId);
      if (response.data.success) {
        setUserAttempts(response.data.attempts);
      }
    } catch (error) {
      console.error('Error fetching user attempts:', error);
    }
  };

  // Function to download the current webpage as a PDF  
  const downloadPdf = () => {  
    const element = document.getElementById('result-container');
    const opt = {
      margin: 10,
      filename: `quiz-result-${formatDate(quizData.date)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };  

  // Calculate percentage
  const percentage = ((score / quizData.questions.length) * 100).toFixed(2);
  
  // Determine performance level
  const getPerformanceLevel = () => {
    if (percentage >= 80) return { text: 'Excellent! 🎉', color: '#28a745' };
    if (percentage >= 60) return { text: 'Good Job! 👍', color: '#17a2b8' };
    if (percentage >= 40) return { text: 'Keep Practicing! 💪', color: '#ffc107' };
    return { text: 'Need More Practice 📚', color: '#dc3545' };
  };

  const performance = getPerformanceLevel();

  // Render a message if quizData is not yet loaded or responses are empty  
  if (!quizData || responses.length === 0) {  
    return (
      <>
        <Navbar />
        <div className="center-container">
          <div className="no-result-card">
            <h2>No Quiz Result Found</h2>
            <p>Please complete a quiz first to see your results.</p>
            <button 
              onClick={() => navigate('/premium')}
              className="btn btn-primary"
            >
              Browse Premium Batches
            </button>
          </div>
        </div>
      </>
    );
  }  

  return (  
    <>  
      <Navbar />  
      <div className="result-page">
        {/* Result Header */}
        <div className="result-header">
          <div className="container">
            <h1 className="result-heading">📊 Quiz Result</h1>
            
            {batch && (
              <p className="batch-name">
                Batch: <strong>{batch.name}</strong> | Subject: <strong>{quizData.subject}</strong>
              </p>
            )}
            <p className="quiz-date">Date: {formatDate(quizData.date)}</p>
          </div>
        </div>

        <div className="result-container" id="result-container">  
          {/* Score Card */}
          <div className="score-card">
            <div className="score-main">
              <h2 className="score-text">Your Score</h2>
              <div className="score-display">
                <span className="score-value">{score * 2}</span>
                <span className="score-total">/ {quizData.questions.length * 2}</span>
              </div>
              <div className="percentage-display">
                <div className="percentage-circle" style={{ borderColor: performance.color }}>
                  <span style={{ color: performance.color }}>{percentage}%</span>
                </div>
              </div>
              <p className="performance-text" style={{ color: performance.color }}>
                {performance.text}
              </p>
            </div>

            <div className="score-stats">
              <div className="stat-item">
                <span className="stat-label">Total Questions</span>
                <span className="stat-value">{quizData.questions.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Correct Answers</span>
                <span className="stat-value correct">{score}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Wrong Answers</span>
                <span className="stat-value wrong">{quizData.questions.length - score}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Accuracy</span>
                <span className="stat-value">{percentage}%</span>
              </div>
            </div>

            <div className="action-buttons">
              <button onClick={downloadPdf} className="btn btn-success">
                📥 Download PDF
              </button>
              <button 
                onClick={() => navigate(`/premium-tests/${batchId}`)}
                className="btn btn-primary"
              >
                📝 More Quizzes
              </button>
              <button 
                onClick={() => navigate(`/premium-batch/${batchId}`)}
                className="btn btn-primary"
              >
                ℹ️ Batch Details
              </button>
            </div>
          </div>  

          {/* Answers Section */}
          <div className="answers-section">
            <h2 className="section-title">📋 Detailed Solutions</h2>
            <div className="quiz-container">  
              {quizData.questions.map((question, id) => (  
                <React.Fragment key={id}>  
                  <div className="question-container">  
                    <Answer  
                      question={question}  
                      id={id}  
                      selected={responses[id]}  
                      answer={quizData.questions[id].answer}  
                    />  
                  </div>  
                </React.Fragment>  
              ))}  
            </div>
          </div>

          {/* User's Previous Attempts */}
          {userAttempts.length > 0 && (
            <div className="attempts-section">
              <h2 className="section-title">📈 Your Previous Attempts</h2>
              <div className="attempts-list">
                {userAttempts.slice(0, 5).map((attempt, index) => (
                  <div key={index} className="attempt-card">
                    <div className="attempt-info">
                      <span className="attempt-number">Attempt #{attempt.attemptNumber}</span>
                      <span className="attempt-date">
                        {new Date(attempt.attemptDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="attempt-score">
                      <span className="score">{attempt.score}/{attempt.totalQuestions * 2}</span>
                      <span className="percentage">
                        ({((attempt.correctAnswers / attempt.totalQuestions) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* More Quizzes from Same Batch */}
          {batchQuizzes.length > 0 && (
            <div className="more-quizzes-section">
              <h2 className="section-title">🎯 More Quizzes from {batch?.name}</h2>
              {loading ? (
                <Spinner />
              ) : (
                <div className="quizzes-list">
                  {batchQuizzes.slice(0, 5).map(quiz => (
                    <Link 
                      to={`/get-premium-quiz/${batchId}/${quiz._id}`} 
                      key={quiz._id} 
                      className="quiz-link"
                    >
                      <div className="quiz-item">
                        <div className="quiz-info">
                          <strong className="quiz-subject">{quiz.subject}</strong>
                          <span className="quiz-date">{formatDate(quiz.date)}</span>
                        </div>
                        <div className="quiz-meta">
                          <span className="question-count">
                            {quiz.questionCount || 0} Questions
                          </span>
                          <span className="start-arrow">→</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>  
  );  
}